import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { CheckCircle } from 'lucide-react';

interface FormData {
  first_name: string;
  last_name: string;
  institution: string;
  license_number: string;
  email: string;
  attendance_type: string;
  payment_type: string;
  sponsor_name: string;
}

export function SymposiumRegistrationPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccessful, setSubmissionSuccessful] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    institution: '',
    license_number: '',
    email: '',
    attendance_type: '',
    payment_type: '',
    sponsor_name: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('symposium_attendees')
        .insert([formData]);

      if (error) throw error;

      toast.success('Uspješno ste se prijavili na simpozij!');
      setSubmittedData({...formData});
      setSubmissionSuccessful(true);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Došlo je do greške prilikom prijave. Molimo pokušajte ponovno.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Komponenta za prikaz potvrde prijave
  const RegistrationConfirmation = () => {
    if (!submittedData) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-600 to-green-800 text-white p-8">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-center">
            Uspješna prijava na simpozij!
          </h1>
          <p className="text-green-100 text-center mt-2">
            Hvala vam na vašoj prijavi za simpozij "Multidisciplinarni pristup u liječenju tumora probavnih organa"
          </p>
        </div>

        <div className="p-8">
          <div className="mb-8 bg-green-50 p-6 rounded-lg border border-green-200">
            <p className="text-gray-800">
              Vaša prijava je uspješno zaprimljena. Svi podaci su pohranjeni u našoj bazi podataka.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-6">Pregled prijavljenih podataka</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Osobni podaci</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Ime</p>
                    <p className="text-gray-900 font-medium">{submittedData.first_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Prezime</p>
                    <p className="text-gray-900 font-medium">{submittedData.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Institucija</p>
                    <p className="text-gray-900 font-medium">{submittedData.institution}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">HKMS/HLK Broj</p>
                    <p className="text-gray-900 font-medium">{submittedData.license_number}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Kontakt podaci</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-gray-900 font-medium">{submittedData.email}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Način sudjelovanja</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 font-medium">
                  {submittedData.attendance_type === 'online' ? 'Online' : 'Uživo'}
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Podaci o plaćanju</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 font-medium">
                  Trošak kotizacije snosi: {
                    submittedData.payment_type === 'personal' ? 'Osobno' :
                    submittedData.payment_type === 'institution' ? 'Zdravstvena ustanova' :
                    'Sponzor'
                  }
                </p>
                {submittedData.payment_type === 'sponsor' && submittedData.sponsor_name && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">Ime sponzora</p>
                    <p className="text-gray-900 font-medium">{submittedData.sponsor_name}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setSubmissionSuccessful(false)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Napravi novu prijavu
            </button>
            <button
              onClick={() => navigate('/simpozij')}
              className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Povratak na početnu stranicu
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-4"
      >
        {submissionSuccessful ? (
          <RegistrationConfirmation />
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
              <h1 className="text-3xl font-bold text-center">Prijava na simpozij</h1>
              <p className="text-blue-100 text-center mt-2">
                "Multidisciplinarni pristup u liječenju tumora probavnih organa"
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* Personal Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900">Osobni podaci</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ime
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prezime
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institucija
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="institution"
                    required
                    value={formData.institution}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HKMS/HLK Broj
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="license_number"
                    required
                    value={formData.license_number}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900">Kontakt podaci</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </motion.div>

              {/* Attendance Type */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900">Način sudjelovanja
                  <span className="text-red-500 ml-1">*</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { value: 'online', label: 'Online' },
                    { value: 'inperson', label: 'Uživo' }
                  ].map((option) => (
                    <div 
                      key={option.value}
                      className={`
                        border rounded-lg p-4 text-center cursor-pointer transition-all duration-200
                        ${formData.attendance_type === option.value 
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }
                      `}
                      onClick={() => setFormData(prev => ({ ...prev, attendance_type: option.value }))}
                    >
                      <input 
                        type="radio"
                        name="attendance_type"
                        value={option.value}
                        checked={formData.attendance_type === option.value}
                        onChange={handleInputChange}
                        className="sr-only"
                        required={formData.attendance_type === ''}
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Payment Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold text-gray-900">Trošak kotizacije snosi
                  <span className="text-red-500 ml-1">*</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'personal', label: 'Osobno' },
                    { value: 'institution', label: 'Zdravstvena ustanova' },
                    { value: 'sponsor', label: 'Sponzor' }
                  ].map((option) => (
                    <div 
                      key={option.value}
                      className={`
                        border rounded-lg p-4 text-center cursor-pointer transition-all duration-200
                        ${formData.payment_type === option.value 
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }
                      `}
                      onClick={() => setFormData(prev => ({ ...prev, payment_type: option.value }))}
                    >
                      <input 
                        type="radio"
                        name="payment_type"
                        value={option.value}
                        checked={formData.payment_type === option.value}
                        onChange={handleInputChange}
                        className="sr-only"
                        required={formData.payment_type === ''}
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </div>
                  ))}
                </div>
                {formData.payment_type === 'sponsor' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3"
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ime sponzora
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="sponsor_name"
                      placeholder="Naziv sponzora"
                      value={formData.sponsor_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={formData.payment_type === 'sponsor'}
                    />
                  </motion.div>
                )}
              </motion.div>

              {/* Submit Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="pt-6"
              >
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-4 px-8 text-lg font-bold text-white bg-blue-600 rounded-full 
                    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                    transition-colors duration-200`}
                >
                  {isSubmitting ? 'Slanje prijave...' : 'Pošalji prijavu'}
                </button>
              </motion.div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
} 